import { getBadgeGl } from './badge-gl-registry.js'

/** Max CSS tilt in degrees from centre to edge (the prototype's SVG fallback: 18° across).
 *  With WebGL the 3D engine rotates the badge itself (±0.25 / ±0.31 rad), see badge-gl.ts. */
const MAX_DEG = 9

export interface TiltRect {
    left: number
    top: number
    width: number
    height: number
}

const clamp01 = (v: number) => Math.max(0, Math.min(1, v))

/** Pointer position inside the badge box → rotateX/rotateY degrees (top-right = up/right). */
export const computeTilt = (rect: TiltRect, clientX: number, clientY: number) => {
    if (!rect.width || !rect.height) return { rx: 0, ry: 0 }
    const px = clamp01((clientX - rect.left) / rect.width)
    const py = clamp01((clientY - rect.top) / rect.height)
    return {
        rx: Number(((0.5 - py) * 2 * MAX_DEG).toFixed(2)),
        ry: Number(((px - 0.5) * 2 * MAX_DEG).toFixed(2)),
    }
}

/* viewBox is -14 -12 228 236; the renderer's fixed light sits at (30, 4) and the
   environment strip at (-50, -52) — see src/common/badge-art/render.ts. */
const VB = { x: -14, y: -12, w: 228, h: 236 }
const ENV_SHIFT = 18

export interface BadgeLight {
    x: number
    y: number
    envX: number
    envY: number
}

export const REST_LIGHT: BadgeLight = { x: 30, y: 4, envX: -50, envY: -52 }

const REST_P = { x: (REST_LIGHT.x - VB.x) / VB.w, y: (REST_LIGHT.y - VB.y) / VB.h }

/**
 * Pointer position → fePointLight position in viewBox units and the environment
 * image offset (the "reflection" slides against the pointer), as in the prototype.
 */
export const computeLight = (rect: TiltRect, clientX: number, clientY: number): BadgeLight => {
    if (!rect.width || !rect.height) return REST_LIGHT
    const px = clamp01((clientX - rect.left) / rect.width)
    const py = clamp01((clientY - rect.top) / rect.height)
    const round = (v: number) => Number(v.toFixed(2))
    return {
        x: round(VB.x + px * VB.w),
        y: round(VB.y + py * VB.h),
        envX: round(REST_LIGHT.envX + (REST_P.x - px) * ENV_SHIFT),
        envY: round(REST_LIGHT.envY + (REST_P.y - py) * ENV_SHIFT),
    }
}

/** The DOM surface the helpers touch — narrow on purpose so tests can pass a fake. */
export interface TiltElement {
    classList: { add: (c: string) => void; remove: (c: string) => void }
    style: { setProperty: (k: string, v: string) => void }
    getBoundingClientRect: () => TiltRect
    querySelectorAll: (
        selector: string,
    ) => ArrayLike<{ setAttribute: (k: string, v: string) => void }>
}

/** Tracks the pointer with a short transition; the default 550 ms ease brings it back. */
export const LIVE_CLASS = 'badge-art--live'

/** Move the SVG's point light and reflection strip so the metal highlight follows the pointer. */
const applyLight = (el: TiltElement, light: BadgeLight) => {
    Array.from(el.querySelectorAll('fePointLight')).forEach((l) => {
        l.setAttribute('x', String(light.x))
        l.setAttribute('y', String(light.y))
    })
    Array.from(el.querySelectorAll('feImage')).forEach((img) => {
        img.setAttribute('x', String(light.envX))
        img.setAttribute('y', String(light.envY))
    })
}

/** Rest-pose ease-back duration (matches the CSS transition on .badge-art--tilt). */
export const REST_MS = 550
/** Ease-in from rest when the pointer first arrives, so the first tilt is not a jump. */
export const ENTER_MS = 300

/** Injectable clock so the easing is testable; `null` = apply instantly (no animation frames). */
export interface TiltClock {
    now: () => number
    raf: (cb: () => void) => void
}

const defaultClock = (): TiltClock | null =>
    typeof requestAnimationFrame === 'function' && typeof performance !== 'undefined'
        ? { now: () => performance.now(), raf: (cb) => void requestAnimationFrame(cb) }
        : null

interface Pose {
    rx: number
    ry: number
    light: BadgeLight
}

interface TiltState {
    /** What is currently applied to the element. */
    pose: Pose
    /** Where the pointer wants the badge (updated on every move). */
    target: Pose
    /** Ease-in from rest in progress: moves only retarget it. */
    entering: boolean
    /** True once the ease-in finished: moves are applied directly. */
    live: boolean
    /** Bumped whenever the mode changes so a stale animation frame knows to stop. */
    token: number
}

const REST_POSE: Pose = { rx: 0, ry: 0, light: REST_LIGHT }
const states = new WeakMap<TiltElement, TiltState>()

const getState = (el: TiltElement): TiltState => {
    let state = states.get(el)
    if (!state) {
        state = { pose: REST_POSE, target: REST_POSE, entering: false, live: false, token: 0 }
        states.set(el, state)
    }
    return state
}

const applyPose = (el: TiltElement, state: TiltState, pose: Pose) => {
    state.pose = pose
    el.style.setProperty('--rx', `${pose.rx}deg`)
    el.style.setProperty('--ry', `${pose.ry}deg`)
    applyLight(el, pose.light)
}

const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3)
const mix = (a: number, b: number, t: number) => Number((a + (b - a) * t).toFixed(2))
const mixPose = (a: Pose, b: Pose, k: number): Pose => ({
    rx: mix(a.rx, b.rx, k),
    ry: mix(a.ry, b.ry, k),
    light: {
        x: mix(a.light.x, b.light.x, k),
        y: mix(a.light.y, b.light.y, k),
        envX: mix(a.light.envX, b.light.envX, k),
        envY: mix(a.light.envY, b.light.envY, k),
    },
})

/**
 * Tilt a badge-art element toward a pointer position. The position is mapped into the
 * badge's own box and clamped, so a wider scope (the whole card) works too. The first
 * move after rest eases in over ENTER_MS; later moves are followed directly (CSS 60 ms).
 */
export const tiltElement = (
    el: TiltElement,
    clientX: number,
    clientY: number,
    clock: TiltClock | null = defaultClock(),
): void => {
    const rect = el.getBoundingClientRect()
    const gl = getBadgeGl(el)
    if (gl) {
        if (rect.width && rect.height) {
            gl.tilt(
                clamp01((clientX - rect.left) / rect.width),
                clamp01((clientY - rect.top) / rect.height),
            )
        }
        return
    }
    const { rx, ry } = computeTilt(rect, clientX, clientY)
    const state = getState(el)
    state.target = { rx, ry, light: computeLight(rect, clientX, clientY) }
    el.classList.add(LIVE_CLASS)
    if (state.entering) return
    const token = ++state.token
    if (state.live || !clock) {
        state.live = true
        applyPose(el, state, state.target)
        return
    }
    state.entering = true
    const from = state.pose
    const t0 = clock.now()
    const step = () => {
        if (state.token !== token) return
        const t = Math.min(1, (clock.now() - t0) / ENTER_MS)
        if (t >= 1) {
            state.entering = false
            state.live = true
            applyPose(el, state, state.target)
            return
        }
        applyPose(el, state, mixPose(from, state.target, easeOutCubic(t)))
        clock.raf(step)
    }
    clock.raf(step)
}

/**
 * Ease back to the rest pose and the fixed studio light over REST_MS (ease-out), rotation
 * and light together; the SVG light has no CSS transition, so this runs on animation frames.
 */
export const restElement = (el: TiltElement, clock: TiltClock | null = defaultClock()): void => {
    const gl = getBadgeGl(el)
    if (gl) {
        gl.release()
        return
    }
    const state = getState(el)
    const from = state.pose
    const token = ++state.token
    state.live = false
    state.entering = false
    state.target = REST_POSE
    const finish = () => {
        el.classList.remove(LIVE_CLASS)
        applyPose(el, state, REST_POSE)
    }
    if (!clock) {
        finish()
        return
    }
    const t0 = clock.now()
    const step = () => {
        if (state.token !== token) return
        const t = Math.min(1, (clock.now() - t0) / REST_MS)
        if (t >= 1) {
            finish()
            return
        }
        applyPose(el, state, mixPose(from, REST_POSE, easeOutCubic(t)))
        clock.raf(step)
    }
    clock.raf(step)
}
